import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Update, Send, Remove, Get } from '../../services/service.api';
import { toast } from 'react-toastify';
import { Edit2, Plus, Trash2, X, Calendar, RotateCcw, Search, ArrowDown, ArrowUp } from 'lucide-react';
import ConfirmModal from '../../components/component.confirmModal';
import FullScreenLoader from '../../components/FullScreenLoader';
import Pagination, { usePageSize } from '../../components/component.pagination';

function formatFCFA(n) { return `${Number(n || 0).toLocaleString('fr-FR')} FCFA`; }

function PurchaseModal({ onClose, onSave, t, token, activeCompany, initialData = null }) {
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [form, setForm] = useState(initialData ? {
    ...initialData,
    date: initialData.date ? initialData.date.split('T')[0] : new Date().toISOString().split('T')[0]
  } : {
    produit_id: '',
    produit_libelle: '',
    quantite: '',
    prix_unitaire: '',
    campagne_id: '',
    date: new Date().toISOString().split('T')[0]
  });
  const total = Number(form.quantite || 0) * Number(form.prix_unitaire || 0);

  useEffect(() => {
    setLoadingData(true);
    Promise.all([
      Get(`/products?_t=${Date.now()}`, token),
      Get(`/campaigns?_t=${Date.now()}`, token),
    ]).then(([prodRes, campRes]) => {
      if (Array.isArray(prodRes)) setProducts(prodRes);
      if (Array.isArray(campRes)) setCampaigns(campRes);
    }).catch(() => {}).finally(() => setLoadingData(false));
  }, [token, activeCompany]);

  const handleProductChange = (e) => {
    const product = products.find(p => p.id === e.target.value);
    setForm({ ...form, produit_id: e.target.value, produit_libelle: product?.libelle || '' });
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.produit_id || !form.quantite || !form.prix_unitaire || !form.campagne_id) {
      return toast.error('Veuillez remplir tous les champs obligatoires (incluant la campagne)');
    }

    setSaving(true);
    try {
      await onSave({ ...form, total });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{initialData ? 'Modifier l\'achat' : t.purchases.add}</h2>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{t.purchases.produit} <span className="required">*</span></label>
              {loadingData ? (
                <div className="skeleton" style={{ height: 38, borderRadius: 'var(--radius-sm)' }} />
              ) : (
                <select className="form-select" value={form.produit_id} onChange={handleProductChange} required>
                  <option value="">-- Sélectionner un produit --</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                </select>
              )}
              {!loadingData && products.length === 0 && (
                <small style={{ color: 'var(--warning)', marginTop: 4, display: 'block' }}>
                  ⚠ Aucun produit disponible. Créez d'abord un produit.
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">{t.purchases.campagne || 'Campagne associée (optionnel)'}</label>
              {loadingData ? (
                <div className="skeleton" style={{ height: 38, borderRadius: 'var(--radius-sm)' }} />
              ) : (
                <select
                  className="form-select"
                  value={form.campagne_id}
                  onChange={e => setForm({ ...form, campagne_id: e.target.value })}
                  required
                >
                  <option value="">-- Sélectionnez une campagne --</option>
                  {campaigns.map(c => (
                    <option key={c.id} value={c.id}>{c.libelle}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t.purchases.quantite} <span className="required">*</span></label>
                <input
                  type="number" min="1" className="form-control" required
                  value={form.quantite}
                  onChange={e => setForm({ ...form, quantite: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t.purchases.prixUnitaire} <span className="required">*</span></label>
                <input
                  type="number" min="0" className="form-control" required
                  value={form.prix_unitaire}
                  onChange={e => setForm({ ...form, prix_unitaire: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t.purchases.date}</label>
              <input
                type="date"
                className="form-control"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
              />
            </div>

            {/* Total calculé */}
            <div style={{
              background: 'var(--bg-input)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '12px 14px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{t.purchases.total}</span>
              <strong style={{ color: 'var(--primary)', fontSize: '1.1rem' }} className="number-format">
                {formatFCFA(total)}
              </strong>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : t.common.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Purchases() {
  const { t } = useTranslation();
  const token = useSelector(s => s.userReducer.token);
  const activeCompany = useSelector(s => s.userReducer.activeCompany);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPurchase, setEditPurchase] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('purchases');
  const [campaigns, setCampaigns] = useState([]);
  const [filterCampagne, setFilterCampagne] = useState('');

  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  const abortControllerRef = useRef(null);

  useEffect(() => {
    Get(`/campaigns?_t=${Date.now()}`, token)
      .then(r => Array.isArray(r) && setCampaigns(r))
      .catch(() => {});
  }, [token, activeCompany?.id]);

  // Back to page 1 whenever the result set changes
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterCampagne, filterDateDebut, filterDateFin, sortOrder, pageSize, activeCompany?.id]);

  const fetchPurchases = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    try {
      const companyId = activeCompany?.id || '';
      const headers = { authorization: "Bearer " + token, 'Content-Type': 'application/json' };
      if (companyId) headers['X-Company-Id'] = companyId;

      const params = new URLSearchParams();
      if (filterDateDebut) params.append('date_debut', filterDateDebut);
      if (filterDateFin) params.append('date_fin', filterDateFin);
      if (companyId) params.append('company_id', companyId);
      // add a cache buster timestamp to ensure new items show up
      params.append('_t', Date.now());
      const qs = params.toString() ? `?${params.toString()}` : '';

      const API_URL = process.env.REACT_APP_API_URL || "https://savaninvests-backend.onrender.com";
      const response = await fetch(`${API_URL}/purchases${qs}`, { headers, signal });
      const res = await response.json();

      if (!signal.aborted && Array.isArray(res)) {
        setPurchases(res);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // silently fail
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [token, filterDateDebut, filterDateFin, activeCompany?.id]);

  useEffect(() => {
    setPurchases([]); // Clear to prevent ghost data
    fetchPurchases();
  }, [fetchPurchases]);

  useEffect(() => {
    const handleSwitched = () => fetchPurchases();
    window.addEventListener('company-switched', handleSwitched);
    return () => window.removeEventListener('company-switched', handleSwitched);
  }, [fetchPurchases]);

  const handleSave = async (form) => {
    try {
      let res;
      if (form.id) {
        res = await Update(`/purchases/${form.id}`, form, token);
      } else {
        res = await Send('/purchases', form, true, token);
      }
      if (res?.error) { toast.error(res.error); return; }
      toast.success(form.id ? 'Achat modifié ✓' : 'Achat enregistré ✓');
      setShowModal(false);
      setEditPurchase(null);
      fetchPurchases();
    } catch { toast.error('Erreur'); }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await Remove(`/purchases/${deleteId}`, token);
      if (res?.error) return toast.error(res.error);
      toast.success('Achat supprimé ✓');
      setDeleteId(null);
      fetchPurchases();
    } catch { toast.error('Erreur'); }
  };

  const resetFilters = () => {
    setFilterDateDebut('');
    setFilterDateFin('');
    setSearchQuery('');
    setFilterCampagne('');
  };

  const isFilterActive = filterDateDebut || filterDateFin || searchQuery || filterCampagne;

  const filteredPurchases = purchases.filter(p => {
    if (filterCampagne && String(p.campagne_id) !== filterCampagne) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (p.produit_libelle && String(p.produit_libelle).toLowerCase().includes(q)) ||
      (p.campagne_libelle && String(p.campagne_libelle).toLowerCase().includes(q))
    );
  }).sort((a, b) => {
    const dateA = new Date(a.date || a.created_at || 0).getTime();
    const dateB = new Date(b.date || b.created_at || 0).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const totalFCFA = filteredPurchases.reduce((acc, p) => acc + Number(p.total || 0), 0);
    const paginated = filteredPurchases.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="page-content page-enter">
      {loading && <FullScreenLoader message="Chargement des achats..." />}
      <div className="page-header">
        <div>
          <h1>{t.purchases.title}</h1>
          <p>Total : <strong className="number-format" style={{ color: 'var(--gold)' }}>{formatFCFA(totalFCFA)}</strong> ({filteredPurchases.length} achats)</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> {t.purchases.add}
        </button>
      </div>

      {/* Filter Bar */}
      <div className="dashboard-filter-bar mb-4">
        <div className="filter-group-wrapper">
          <div className="search-wrapper" style={{ minWidth: 200, maxWidth: "100%" }}>
            <Search size={14} />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Rechercher produit / campagne..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            style={{ width: 'auto', maxWidth: '100%' }}
            value={filterCampagne}
            onChange={e => setFilterCampagne(e.target.value)}
            aria-label="Filtrer par campagne"
          >
            <option value="">Toutes les campagnes</option>
            {campaigns.map(c => <option key={c.id} value={String(c.id)}>{c.libelle}</option>)}
          </select>
          <button 
            className="btn btn-secondary" 
            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
            style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            {sortOrder === 'desc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
            Date ({sortOrder === 'desc' ? 'Récents' : 'Anciens'})
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={14} style={{ color: 'var(--gold)' }} />
            <input
              type="date"
              className="filter-input-date"
              value={filterDateDebut}
              onChange={e => setFilterDateDebut(e.target.value)}
              title="Date début"
            />
            <span style={{ color: 'var(--text-muted)' }}>→</span>
            <input
              type="date"
              className="filter-input-date"
              value={filterDateFin}
              onChange={e => setFilterDateFin(e.target.value)}
              title="Date fin"
            />
          </div>

          {isFilterActive && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
              onClick={resetFilters}
            >
              <RotateCcw size={12} />
              Reset
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card">
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8 }} />)}
        </div>
      ) : filteredPurchases.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🛒</div>
          <h3>{t.purchases.noPurchases}</h3>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t.purchases.produit}</th>
                <th>Campagne</th>
                <th>{t.purchases.quantite}</th>
                <th>{t.purchases.prixUnitaire}</th>
                <th>{t.purchases.total}</th>
                <th>{t.purchases.date}</th>
                <th>{t.common.actions}</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(p => (
                <tr key={p.id}>
                  <td><strong>{p.produit_libelle}</strong></td>
                  <td>
                    {p.campagne_libelle ? (
                      <span className="badge badge-gold" style={{ fontSize: '0.75rem' }}>
                        {p.campagne_libelle}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                  <td className="number-format">{p.quantite}</td>
                  <td className="number-format">{formatFCFA(p.prix_unitaire)}</td>
                  <td className="number-format">
                    <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{formatFCFA(p.total)}</span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    {p.date ? new Date(p.date).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => { setEditPurchase(p); setShowModal(true); }}
                        title="Modifier"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setDeleteId(p.id)}
                        title={t.common.delete}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Total</td>
                <td className="number-format" colSpan={3} style={{ fontWeight: 800, color: 'var(--gold)', fontSize: '1rem' }}>
                  {formatFCFA(totalFCFA)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredPurchases.length > 0 && (
        <Pagination
          page={currentPage}
          pageSize={pageSize}
          total={filteredPurchases.length}
          label="achats"
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {showModal && (
        <PurchaseModal
          initialData={editPurchase}
          onClose={() => { setShowModal(false); setEditPurchase(null); }}
          onSave={handleSave}
          t={t}
          token={token}
          activeCompany={activeCompany}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Supprimer cet achat"
        message="Êtes-vous sûr de vouloir supprimer cet achat ? Cette opération mettra à jour le stock et l'historique."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
